import { AlertTriangle, RotateCcw } from "lucide-react";
import {
  Component,
  useEffect,
  useState,
  type ErrorInfo,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useRouteError } from "react-router-dom";
import { Button } from "@/shared/ui/Button";

/**
 * Stale-bundle recovery + a control-room error screen.
 *
 * **The failure.** The SPA is code-split: every page is a hashed chunk
 * (`/assets/EvacuationPage-<hash>.js`) referenced by the `index.html` the tab
 * loaded. Deploying replaces both, so a tab that was already open keeps asking
 * for chunk hashes that no longer exist on disk → the dynamic import rejects
 * with "Failed to fetch dynamically imported module" and React Router's default
 * screen ("Unexpected Application Error! … 💿 Hey developer") takes over the
 * whole window. On an operations console that means the operator's screen dies
 * mid-shift because someone shipped a build.
 *
 * **The fix.** Any chunk-load rejection is treated as "your bundle is stale":
 * drop the precached shell, then reload once — the tab comes back on the new
 * build with no operator action. The reload is guarded by a **session flag** so
 * a genuinely broken deploy degrades to a readable error card instead of an
 * infinite refresh loop. Everything else renders that same card with 다시 시도.
 *
 * Wired in two places (see `router.tsx`):
 *   * inside `<Suspended>` — catches a lazy page failing to load while keeping
 *     the AppShell (sidebar/top bar) mounted,
 *   * as the routes' `errorElement` — the catch-all that replaces React
 *     Router's developer-facing default.
 */

const RELOAD_FLAG = "expertgas.staleBundleReload";

/** Chunk/module-load rejections, across browsers and Vite's own preload error. */
export function isStaleBundleError(error: unknown): boolean {
  const msg =
    error instanceof Error
      ? `${error.name} ${error.message}`
      : typeof error === "string"
        ? error
        : "";
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError|Loading chunk \d+ failed|dynamically imported module/i.test(
    msg,
  );
}

/** True when this tab already burned its one automatic recovery reload. */
function alreadyRecovered(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_FLAG) != null;
  } catch {
    // Private mode / storage blocked → assume "already tried" so we can never
    // spin. A manual 다시 시도 still works.
    return true;
  }
}

/**
 * Best-effort purge of the precached app shell, then a single reload. Without
 * the cache purge the service worker can hand the tab the *same* stale
 * index.html and the reload would fail identically.
 */
export async function recoverFromStaleBundle(): Promise<void> {
  try {
    sessionStorage.setItem(RELOAD_FLAG, new Date().toISOString());
  } catch {
    /* storage blocked — the reload below still happens, just unguarded once */
  }
  try {
    if (typeof caches !== "undefined") {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* best effort */
  }
  try {
    if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.update().catch(() => undefined)));
    }
  } catch {
    /* best effort */
  }
  window.location.reload();
}

/** Recover if we can, and report whether recovery is under way. */
function tryRecover(error: unknown): boolean {
  if (!isStaleBundleError(error) || alreadyRecovered()) return false;
  void recoverFromStaleBundle();
  return true;
}

// Vite fires this when a module *preload* 404s — earlier than the import
// rejection, and outside any React boundary. Same stale-bundle situation.
if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (event) => {
    if (!alreadyRecovered()) {
      event.preventDefault();
      void recoverFromStaleBundle();
    }
  });
}

/* ─── screen ──────────────────────────────────────────────────────── */

function ErrorScreen({ error, recovering }: { error: unknown; recovering: boolean }) {
  const { t } = useTranslation();
  const stale = isStaleBundleError(error);
  const detail =
    error instanceof Error ? error.message : typeof error === "string" ? error : "";

  return (
    <div className="grid min-h-[60dvh] place-items-center px-6 py-16">
      <div className="w-full max-w-[520px] rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--bg-elevated)] p-7 text-center shadow-[var(--shadow-2)]">
        <span
          aria-hidden
          className="mx-auto grid h-12 w-12 place-items-center rounded-full"
          style={{
            background: "color-mix(in oklab, var(--warning) 16%, transparent)",
            color: "var(--warning)",
          }}
        >
          <AlertTriangle size={24} strokeWidth={2.1} />
        </span>
        <h1 className="mt-4 text-[19px] font-semibold tracking-[-0.012em] text-[var(--fg)]">
          {t(recovering || stale ? "common.appError.staleTitle" : "common.appError.genericTitle")}
        </h1>
        <p className="mt-1.5 text-[13.5px] leading-relaxed text-[var(--fg-subtle)]">
          {t(
            recovering
              ? "common.appError.recovering"
              : stale
                ? "common.appError.staleDesc"
                : "common.appError.genericDesc",
          )}
        </p>
        {detail && !recovering && (
          <p className="mt-3 break-words rounded-[var(--radius-md)] bg-[var(--bg-subtle)] px-3 py-2 text-left text-[11.5px] text-[var(--fg-muted)]">
            {detail}
          </p>
        )}
        <div className="mt-5 flex items-center justify-center gap-2">
          <Button
            intent="primary"
            size="md"
            onClick={() => window.location.reload()}
            leading={<RotateCcw size={15} strokeWidth={1.9} />}
          >
            {t("common.retry")}
          </Button>
          <Button intent="outline" size="md" onClick={() => window.location.assign("/")}>
            {t("common.appError.goHome")}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─── route-level catch-all ───────────────────────────────────────── */

/** `errorElement` for the router — replaces React Router's default screen. */
export function RouteError() {
  const error = useRouteError();
  // Recovery is a side effect (cache purge + reload) — never run it in render.
  const [recovering, setRecovering] = useState(
    () => isStaleBundleError(error) && !alreadyRecovered(),
  );
  useEffect(() => {
    setRecovering(tryRecover(error));
  }, [error]);
  return <ErrorScreen error={error} recovering={recovering} />;
}

/* ─── render-level boundary (keeps the AppShell alive) ─────────────── */

interface BoundaryState {
  error: unknown;
  recovering: boolean;
}

/**
 * Wraps every lazily-loaded page (via `<Suspended>`) so a stale chunk takes out
 * only the page area — the sidebar and top bar stay usable while the tab
 * reloads itself onto the new build.
 */
export class ChunkErrorBoundary extends Component<PropsWithChildren, BoundaryState> {
  override state: BoundaryState = { error: null, recovering: false };

  static getDerivedStateFromError(error: unknown): Partial<BoundaryState> {
    return { error };
  }

  override componentDidCatch(error: unknown, _info: ErrorInfo) {
    const recovering = tryRecover(error);
    if (recovering) this.setState({ recovering: true });
    if (!isStaleBundleError(error)) console.error("[route]", error);
  }

  override render(): ReactNode {
    if (this.state.error != null) {
      return <ErrorScreen error={this.state.error} recovering={this.state.recovering} />;
    }
    return this.props.children;
  }
}
